export const SEND_QA_FAILURE_STAGE = Object.freeze({
  DOCUMENT_STATUS: "document-status",
  QA_DATA_PREPARATION: "qa-data-preparation",
  QA_DATA_SAVE: "qa-data-save",
  DELIVERY_REQUEST: "delivery-request",
  BATCH_FINALIZATION: "batch-finalization",
  QA_COMPLETE_FINALIZATION: "qa-complete-finalization",
});

export const SEND_QA_FAILURE_CODE = Object.freeze({
  ALREADY_PROCESSING: "already-processing",
  REQUEST_REJECTED: "request-rejected",
  AUTHENTICATION: "authentication",
  SOURCE_DOCUMENT: "source-document",
  QA_DATA_PREPARATION: "qa-data-preparation",
  BATCH_NOT_READY: "batch-not-ready",
  AUTHORIZATION: "authorization",
  RATE_LIMITED: "rate-limited",
  PAYLOAD_TOO_LARGE: "payload-too-large",
  DELIVERY_SERVICE: "delivery-service",
  DELIVERY_TIMEOUT: "delivery-timeout",
  ENDPOINT_NOT_FOUND: "endpoint-not-found",
  DELIVERY_CONFIGURATION: "delivery-configuration",
  CONFIGURATION_OR_DELIVERY: "configuration-or-delivery",
  UNEXPECTED: "unexpected",
});

const FAILURE_STAGE_MESSAGES = Object.freeze({
  [SEND_QA_FAILURE_STAGE.DOCUMENT_STATUS]:
    "Send QA could not start because the latest document status could not be loaded.",
  [SEND_QA_FAILURE_STAGE.QA_DATA_PREPARATION]:
    "Send QA could not start while preparing the QA data.",
  [SEND_QA_FAILURE_STAGE.QA_DATA_SAVE]:
    "Send QA could not start because the prepared QA data could not be saved.",
  [SEND_QA_FAILURE_STAGE.DELIVERY_REQUEST]:
    "Send QA could not be accepted by the configured delivery process.",
  [SEND_QA_FAILURE_STAGE.BATCH_FINALIZATION]:
    "Batch QA completion did not finish, so Send QA was not attempted. Some documents may already have changed.",
  [SEND_QA_FAILURE_STAGE.QA_COMPLETE_FINALIZATION]:
    "The final QA Complete save did not finish, so Send QA was not attempted.",
});

const DOCUMENT_STATUS_LABELS = new Map(
  [
    "Uploaded",
    "In Progress",
    "Complete",
    "QA Uploaded",
    "QA In Progress",
    "QA Complete",
    "Rejected",
    "Failed",
  ].map((status) => [status.toLowerCase(), status]),
);

const FAILURE_CODE_MESSAGES = Object.freeze({
  [SEND_QA_FAILURE_CODE.REQUEST_REJECTED]:
    "Send QA was rejected before delivery details were returned.",
  [SEND_QA_FAILURE_CODE.ALREADY_PROCESSING]:
    "Send QA is already queued or processing. Wait for it to finish before completing QA.",
  [SEND_QA_FAILURE_CODE.AUTHENTICATION]:
    "Send QA could not start because your authenticated session could not be verified.",
  [SEND_QA_FAILURE_CODE.SOURCE_DOCUMENT]:
    "Send QA could not start because the source document details could not be resolved.",
  [SEND_QA_FAILURE_CODE.QA_DATA_PREPARATION]:
    "Send QA could not start because the QA data could not be prepared for delivery.",
  [SEND_QA_FAILURE_CODE.BATCH_NOT_READY]:
    "Send QA could not start because batch completion is still in progress.",
  [SEND_QA_FAILURE_CODE.AUTHORIZATION]:
    "Send QA was rejected because the configured delivery authorization was not accepted.",
  [SEND_QA_FAILURE_CODE.RATE_LIMITED]:
    "Send QA was temporarily rate limited by the configured delivery process.",
  [SEND_QA_FAILURE_CODE.PAYLOAD_TOO_LARGE]:
    "Send QA could not be handed to the delivery service because the generated payload was too large.",
  [SEND_QA_FAILURE_CODE.DELIVERY_SERVICE]:
    "The Send QA delivery service could not complete the request.",
  [SEND_QA_FAILURE_CODE.DELIVERY_TIMEOUT]:
    "The Send QA delivery process timed out before it completed.",
  [SEND_QA_FAILURE_CODE.ENDPOINT_NOT_FOUND]:
    "Send QA could not be delivered because the configured endpoint was not found.",
  [SEND_QA_FAILURE_CODE.DELIVERY_CONFIGURATION]:
    "Send QA could not start because no applicable delivery transmitter is configured.",
  [SEND_QA_FAILURE_CODE.CONFIGURATION_OR_DELIVERY]:
    "Send QA was rejected by the configured delivery process.",
});

export function createSendQaFlowError(code, details = {}) {
  const error = new Error("Send QA workflow did not complete");
  error.name = "SendQaFlowError";
  error.code = Object.values(SEND_QA_FAILURE_CODE).includes(code)
    ? code
    : SEND_QA_FAILURE_CODE.UNEXPECTED;
  error.diagnosticReference = details?.diagnosticReference;
  error.delivery = details?.delivery;
  return error;
}

export function getSendQaFailureCode(error) {
  return Object.values(SEND_QA_FAILURE_CODE).includes(error?.code)
    ? error.code
    : SEND_QA_FAILURE_CODE.UNEXPECTED;
}

export function normalizeSendQaDocumentStatus(status) {
  return DOCUMENT_STATUS_LABELS.get(String(status || "").trim().toLowerCase()) || null;
}

export async function persistBeforeQaCompleteDelivery({ persist, deliver }) {
  await persist();
  return deliver();
}

export function buildSendQaFailureNotice({
  stage,
  code,
  documentStatus,
  reference,
  delivery,
}) {
  const normalizedCode = Object.values(SEND_QA_FAILURE_CODE).includes(code)
    ? code
    : SEND_QA_FAILURE_CODE.UNEXPECTED;
  const normalizedStatus = normalizeSendQaDocumentStatus(documentStatus);
  const qaCompletePersistedBeforeDelivery =
    stage === SEND_QA_FAILURE_STAGE.DELIVERY_REQUEST &&
    normalizedStatus === "QA Complete";
  const stageMessage = FAILURE_CODE_MESSAGES[normalizedCode] ||
    FAILURE_STAGE_MESSAGES[stage] ||
    "Send QA could not be completed because an unexpected workflow error occurred.";
  const persistenceMessage = qaCompletePersistedBeforeDelivery
    ? "The QA Complete transition was saved before delivery was attempted and must not be repeated."
    : "";
  const statusMessage = normalizedStatus
    ? `The latest saved document status is ${normalizedStatus}.`
    : "The latest saved document status could not be verified.";
  const transmitterType = ["rest", "auth-rest", "sftp", "legacy"].includes(delivery?.transmitterType)
    ? delivery.transmitterType.toUpperCase()
    : "delivery process";
  const statusCode = Number(delivery?.statusCode);
  const attemptsUsed = Math.max(0, Number(delivery?.attemptsUsed) || 0);
  const configuredRetryCount = Math.max(0, Number(delivery?.configuredRetryCount) || 0);
  const deliveryEvidence = attemptsUsed > 0
    ? `Delivery result: ${transmitterType}${Number.isInteger(statusCode) && statusCode > 0 ? ` returned HTTP ${statusCode}` : " did not return a successful status"} after ${attemptsUsed} ${attemptsUsed === 1 ? "attempt" : "attempts"}${configuredRetryCount > 0 ? ` (${configuredRetryCount} configured ${configuredRetryCount === 1 ? "retry" : "retries"})` : ""}.`
    : "";
  let actionMessage = "Reload the document status before retrying. If the problem continues, contact support.";
  if (normalizedCode === SEND_QA_FAILURE_CODE.ALREADY_PROCESSING) {
    actionMessage = "Reload the document status after background processing finishes.";
  } else if (normalizedCode === SEND_QA_FAILURE_CODE.AUTHENTICATION) {
    actionMessage = "Sign in again, reload the document status, and retry.";
  } else if (normalizedCode === SEND_QA_FAILURE_CODE.PAYLOAD_TOO_LARGE) {
    actionMessage = "Contact support to use the scalable delivery handoff before retrying; repeating the same request will not reduce its size.";
  } else if ([
    SEND_QA_FAILURE_CODE.AUTHORIZATION,
    SEND_QA_FAILURE_CODE.ENDPOINT_NOT_FOUND,
    SEND_QA_FAILURE_CODE.DELIVERY_CONFIGURATION,
    SEND_QA_FAILURE_CODE.CONFIGURATION_OR_DELIVERY,
  ].includes(normalizedCode)) {
    actionMessage = "Contact support to correct the delivery configuration before retrying.";
  } else if (delivery?.retryable === true) {
    actionMessage = "Wait briefly, reload the document status, and retry once. If it fails again, contact support.";
  }
  const referenceMessage = reference ? ` Reference: ${reference}.` : "";

  return [stageMessage, persistenceMessage, deliveryEvidence, statusMessage, actionMessage]
    .filter(Boolean)
    .join(" ") + referenceMessage;
}
